import { defineStore } from 'pinia'
import { store } from '../index'
import { BasedataApi } from '@/api/basedata'
import type { DictItemVO } from '@/api/basedata'
import type { DictDataType } from '@/utils/dict'
import type { ElementPlusInfoType } from '@/types/elementPlus'

/** 后端 status 实为数字 1/0，字典契约类型写的是 ENABLED/DISABLED，两者都认 */
const isEnabled = (s: unknown) => s === 1 || s === 'ENABLED'

/** DictItemVO → DictDataType：code 作为 value，name 作为 label；颜色信息暂由 extra 透传 */
const toDictItem = (type: string, item: DictItemVO): DictDataType => ({
  dictType: type,
  value: item.code,
  label: item.name,
  colorType: ((item.extra?.colorType as string) || '') as ElementPlusInfoType | '',
  cssClass: (item.extra?.cssClass as string) || ''
})

/**
 * 在途请求去重：同一类型被并发触发时只发一次请求。
 * 放在模块作用域而不是 state —— 它只是请求簿记，不需要响应式。
 */
const inflight = new Map<string, Promise<void>>()

export const useDictStore = defineStore('dict', {
  state: () => ({
    /** 类型码 → 条目列表。键不存在表示「尚未加载」，空数组表示「已加载但没有启用条目」 */
    dictMap: {} as Record<string, DictDataType[]>
  }),
  getters: {
    getDictMap: (state) => state.dictMap
  },
  actions: {
    /** 拉取单个类型并落库；失败降级为空数组，避免同一类型被反复重试 */
    async loadDictType(type: string): Promise<void> {
      const pending = inflight.get(type)
      if (pending) return pending
      const task = (async () => {
        try {
          // 全量接口不分页，避免分页上限截断大字典（如行政区划 1.2 万条）；
          // 不读公开接口是因为它优先读 Redis 发布快照，未发布的导入数据会缺失
          const res = await BasedataApi.getAllItems(type)
          this.dictMap[type] = (res || [])
            .filter((it) => isEnabled(it.status))
            .map((it) => toDictItem(type, it))
        } catch {
          this.dictMap[type] = []
        } finally {
          inflight.delete(type)
        }
      })()
      inflight.set(type, task)
      return task
    },

    /**
     * 按需加载若干字典类型（已加载/在途的直接跳过），供页面在 setup 里显式声明。
     * 路由守卫不再统一预加载，谁进页面谁触发自己的类型。
     */
    async ensureDictTypes(types: string[]): Promise<void> {
      const missing = types.filter((type) => this.dictMap[type] === undefined)
      if (!missing.length) return
      await Promise.all(missing.map((type) => this.loadDictType(type)))
    },

    /**
     * 不等待结果的单类型触发，供 getDictOptions 在渲染期调用。
     * 已加载或在途时是空操作，所以可以放心地每次渲染都调。
     */
    requestDictType(type: string): void {
      if (!type || this.dictMap[type] !== undefined || inflight.has(type)) return
      void this.loadDictType(type)
    },

    getDictByType(type: string): DictDataType[] {
      return this.dictMap[type] || []
    },

    /** 清空缓存（退出登录等场景）；此后再次读取会重新按需拉取 */
    resetDict() {
      this.dictMap = {}
    }
  }
})

export const useDictStoreWithOut = () => {
  return useDictStore(store)
}
